import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PostCard from '@/components/base/PostCard';
import AdSlot from '@/pages/home/components/AdSlot';
import { getPublishedPosts } from '@/utils/scheduler';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import BreadcrumbSchema from '@/components/base/BreadcrumbSchema';

const BASE_URL = 'https://roameurope.blog';

/** Convert URL slug back to a readable tag label */
function slugToTag(slug: string): string {
  return slug.replace(/-/g, ' ');
}

export default function TagPage() {
  const { tag: tagSlug } = useParams<{ tag: string }>();
  const tagLabel = tagSlug ? slugToTag(tagSlug) : '';

  const published = useMemo(() => getPublishedPosts(), []);

  const filtered = useMemo(
    () =>
      published.filter((p) =>
        p.tags.some(
          (t) => t.toLowerCase().replace(/\s+/g, '-') === (tagSlug ?? '')
        )
      ),
    [published, tagSlug]
  );

  // Collect all unique tags across all published posts for the sidebar
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    published.forEach((p) => p.tags.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [published]);

  useSeoMeta({
    title: `#${tagLabel} — Relationship Articles`,
    description: `Browse all relationship articles tagged "${tagLabel}" on roameurope.blog. Discover expert advice on dating, love, and emotional wellbeing.`,
    url: `/tag/${tagSlug}`,
    type: 'website',
    tags: [tagLabel, 'relationship advice', 'dating tips', 'roameurope.blog'],
  });

  // Schema.org CollectionPage for the tag
  const schemaJson = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `#${tagLabel} — roameurope.blog`,
    description: `Articles tagged "${tagLabel}" on roameurope.blog`,
    url: `${BASE_URL}/tag/${tagSlug}`,
    publisher: {
      '@type': 'Organization',
      name: 'roameurope.blog',
      url: BASE_URL,
    },
    hasPart: filtered.map((post) => ({
      '@type': 'Article',
      headline: post.title,
      url: `${BASE_URL}/post/${post.slug}`,
      image: post.image,
      author: { '@type': 'Person', name: post.author },
    })),
  });

  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: `#${tagLabel}`, path: `/tag/${tagSlug}` },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />
      <main className="pt-24 min-h-screen bg-white dark:bg-stone-950 transition-colors duration-300">
        {/* Tag hero strip */}
        <div className="w-full bg-gradient-to-br from-rose-50 to-stone-50 dark:from-stone-900 dark:to-stone-950 border-b border-stone-100 dark:border-stone-800 mb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 pb-2">
            <BreadcrumbSchema items={breadcrumbItems} />
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex items-center gap-5">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex-shrink-0">
              <i className="ri-price-tag-3-line text-2xl text-rose-500" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-rose-400 mb-1">Tag</p>
              <h1
                className="text-3xl md:text-4xl font-bold text-stone-900 dark:text-stone-50"
                style={{ fontFamily: "'Lora', serif" }}
              >
                #{tagLabel}
              </h1>
              <p className="text-stone-500 dark:text-stone-400 mt-1 text-sm">
                {filtered.length} {filtered.length === 1 ? 'article' : 'articles'} tagged with this topic
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Article grid */}
            <div className="flex-1 min-w-0">
              <AdSlot slot="header" className="mb-8" />

              {filtered.length === 0 ? (
                <div className="text-center py-20 text-stone-400">
                  <i className="ri-price-tag-3-line text-4xl mb-3 block" />
                  <p className="text-lg font-medium mb-2">No articles found for &ldquo;{tagLabel}&rdquo;</p>
                  <Link to="/" className="text-rose-500 hover:underline text-sm cursor-pointer">
                    ← Back to Home
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar: all tags cloud */}
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-xs font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-widest mb-4">
                  Browse All Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((t) => {
                    const tSlug = t.toLowerCase().replace(/\s+/g, '-');
                    const isActive = tSlug === tagSlug;
                    return (
                      <Link
                        key={t}
                        to={`/tag/${tSlug}`}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                          isActive
                            ? 'bg-rose-500 text-white'
                            : 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/20 dark:hover:text-rose-400'
                        }`}
                      >
                        #{t}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}